import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSettings, updateDefaultQuota } from "../../api/settings.api";
import { Button } from "../ui/Button";
import { formatBytes } from "../../utils/formatBytes";
import { Spinner } from "../ui/Spinner";

const PRESETS = [
  { label: "100 MB", bytes: "104857600" },
  { label: "500 MB", bytes: "524288000" },
  { label: "1 GB",   bytes: "1073741824" },
  { label: "5 GB",   bytes: "5368709120" },
  { label: "10 GB",  bytes: "10737418240" },
  { label: "50 GB",  bytes: "53687091200" },
  { label: "100 GB", bytes: "107374182400" },
];

const UNIT_MULTIPLIERS: Record<string, bigint> = {
  MB: 1048576n,
  GB: 1073741824n,
  TB: 1099511627776n,
};

export function DefaultQuotaEditor() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: getSettings,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");
  const [unit, setUnit] = useState("MB");

  const currentBytes = settingsQuery.data?.defaultQuotaBytes ?? "104857600";
  const activeSelected = selected ?? currentBytes;

  const mutation = useMutation({
    mutationFn: () => updateDefaultQuota(activeSelected),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSelected(null);
    },
  });

  const applyCustom = () => {
    const n = parseFloat(customValue);
    if (!isFinite(n) || n <= 0) return;
    const bytes = (BigInt(Math.round(n * 1000)) * UNIT_MULTIPLIERS[unit]!) / 1000n;
    setSelected(bytes.toString());
    setCustomValue("");
  };

  const isUnchanged = activeSelected === currentBytes;

  if (settingsQuery.isLoading) return <Spinner />;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-200">Default Storage for New Users</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Currently: <span className="text-brand-400 font-medium">{formatBytes(currentBytes)}</span>
          {!isUnchanged && (
            <>
              {" "}&rarr;{" "}
              <span className="text-brand-400 font-medium">{formatBytes(activeSelected)}</span>
            </>
          )}
        </p>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.bytes}
            onClick={() => setSelected(p.bytes)}
            className={`rounded px-2 py-0.5 text-xs transition-colors ${
              activeSelected === p.bytes
                ? "bg-brand-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom input */}
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="1"
          step="any"
          placeholder="Custom"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyCustom()}
          className="w-24 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus:border-brand-500 focus:outline-none"
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="rounded border border-gray-600 bg-gray-800 px-1 py-1 text-xs text-gray-100 focus:border-brand-500 focus:outline-none"
        >
          <option value="MB">MB</option>
          <option value="GB">GB</option>
          <option value="TB">TB</option>
        </select>
        <button
          onClick={applyCustom}
          disabled={!customValue}
          className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-300 hover:bg-gray-600 disabled:opacity-40"
        >
          Set
        </button>
      </div>

      <p className="text-[11px] text-gray-500">
        Changes only affect new signups, not existing users.
      </p>

      {/* Save */}
      <Button
        size="sm"
        loading={mutation.isPending}
        onClick={() => mutation.mutate()}
        disabled={isUnchanged}
      >
        Save
      </Button>
    </div>
  );
}
